/* eslint-disable no-console */
import { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useAuthContext } from "../../context/auth";
import { getMovimientosBancarios, getCuentasBancarias, ignorarMovimiento } from "../../utils/movimientosBancarios";
import { Header } from "../Header";
import { Loader } from "../Loader";
import CorregirMovimientoModal from "./CorregirMovimientoModal";
import styles from "./MovimientosBancarios.module.scss";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto, moneda = "CLP") => {
  if (monto == null) return "-";
  const num = Number(monto);
  if (moneda === "USD") return `US$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
  return `$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [, month, day] = fechaStr.split("-");
  return `${day}/${month}`;
};

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const getPeriodoActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const generarPeriodos = () => {
  const periodos = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (val < "2026-01") break;
    periodos.push(val);
  }
  return periodos;
};

const getPeriodoLabel = (periodo) => {
  if (!periodo) return "";
  const [year, month] = periodo.split("-");
  return `${MESES[parseInt(month, 10) - 1]} ${year}`;
};

const periodoToRango = (periodo) => {
  const [year, month] = periodo.split("-").map(Number);
  const desde = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const hasta = `${year}-${String(month).padStart(2, "0")}-${lastDay}`;
  return { desde, hasta };
};

// ─── Componente principal ────────────────────────────────

const MovimientosBancarios = () => {
  const { pais, id: userId } = useAuthContext();

  // Estado
  const [movimientos, setMovimientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroCuenta, setFiltroCuenta] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("pendiente");
  const [filtroPeriodo, setFiltroPeriodo] = useState(getPeriodoActual());
  const [modoFechaCustom, setModoFechaCustom] = useState(false);
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [sincronizando, setSincronizando] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [procesando, setProcesando] = useState(null);
  const [modalCorregir, setModalCorregir] = useState(null);
  const [showCuentasModal, setShowCuentasModal] = useState(false);

  // ─── Carga de datos ────────────────────────────────────

  const cargarDatos = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      const filters = {};
      if (filtroCuenta) filters.cuenta_bancaria_id = Number(filtroCuenta);
      if (filtroTipo) filters.tipo = filtroTipo;
      if (filtroEstado) filters.estado = filtroEstado;
      if (modoFechaCustom) {
        if (filtroFechaDesde) filters.fechaDesde = filtroFechaDesde;
        if (filtroFechaHasta) filters.fechaHasta = filtroFechaHasta;
      } else if (filtroPeriodo) {
        const { desde, hasta } = periodoToRango(filtroPeriodo);
        filters.fechaDesde = desde;
        filters.fechaHasta = hasta;
      }

      const [movs, ctas] = await Promise.all([getMovimientosBancarios(pais, filters), getCuentasBancarias(pais)]);
      setMovimientos(movs);
      setCuentas(ctas);
    } catch (err) {
      console.error("Error cargando movimientos bancarios:", err);
      toast.error("Error cargando movimientos");
    } finally {
      setLoading(false);
    }
  }, [
    pais,
    filtroCuenta,
    filtroTipo,
    filtroEstado,
    filtroPeriodo,
    modoFechaCustom,
    filtroFechaDesde,
    filtroFechaHasta,
  ]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // ─── Resumen ───────────────────────────────────────────

  const resumen = useMemo(() => {
    const ingresos = movimientos.filter((m) => m.tipo === "ingreso");
    const egresos = movimientos.filter((m) => m.tipo === "egreso");
    const pendientes = movimientos.filter((m) => m.estado === "pendiente");
    return {
      totalIngresos: ingresos.reduce((s, m) => s + Number(m.monto), 0),
      totalEgresos: egresos.reduce((s, m) => s + Number(m.monto), 0),
      pendientes: pendientes.length,
      total: movimientos.length,
    };
  }, [movimientos]);

  // ─── Ignorar movimiento ───────────────────────────────

  const handleIgnorar = useCallback(
    async (mov) => {
      setProcesando(mov.id);
      try {
        await ignorarMovimiento(mov.id, userId);
        toast.success("Movimiento ignorado");
        await cargarDatos();
      } catch (err) {
        console.error("Error ignorando:", err);
        toast.error("Error al ignorar");
      } finally {
        setProcesando(null);
      }
    },
    [userId, cargarDatos]
  );

  // ─── Sincronizar con Clay ──────────────────────────────

  const handleSync = useCallback(async () => {
    setSincronizando(true);
    setSyncResult(null);
    try {
      let desde, hasta;
      if (modoFechaCustom) {
        desde = filtroFechaDesde || periodoToRango(getPeriodoActual()).desde;
        hasta = filtroFechaHasta || "";
      } else {
        const rango = periodoToRango(filtroPeriodo || getPeriodoActual());
        desde = rango.desde;
        hasta = rango.hasta;
      }
      let url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/clay-import?pais=${pais}&desde=${desde}`;
      if (hasta) url += `&hasta=${hasta}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      setSyncResult(data);
      const totalNuevos = (data.resultados || []).reduce((s, r) => s + (r.nuevos || 0), 0);
      toast.success(`Sincronización completa: ${totalNuevos} movimientos nuevos`);
      await cargarDatos();
    } catch (err) {
      console.error("Error sincronizando:", err);
      toast.error("Error al sincronizar con Clay");
    } finally {
      setSincronizando(false);
    }
  }, [pais, filtroPeriodo, modoFechaCustom, filtroFechaDesde, filtroFechaHasta, cargarDatos]);

  // ─── Render ────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      <Header title="Movimientos Bancarios">
        <div className={styles.headerResumen}>
          <div className={`${styles.resumenCard} ${styles.ingreso}`}>
            <div className={styles.resumenValor}>{formatMonto(resumen.totalIngresos)}</div>
            <div className={styles.resumenLabel}>Ingresos</div>
          </div>
          <div className={`${styles.resumenCard} ${styles.egreso}`}>
            <div className={styles.resumenValor}>{formatMonto(resumen.totalEgresos)}</div>
            <div className={styles.resumenLabel}>Egresos</div>
          </div>
          <div className={styles.resumenCard}>
            <div className={styles.resumenValor}>{formatMonto(resumen.totalIngresos - resumen.totalEgresos)}</div>
            <div className={styles.resumenLabel}>Saldo neto</div>
          </div>
          <div className={`${styles.resumenCard} ${styles.pendienteCard}`}>
            <div className={styles.resumenValor}>{resumen.pendientes}</div>
            <div className={styles.resumenLabel}>Pendientes</div>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button
            className={styles.btnSync}
            onClick={() => setShowCuentasModal(true)}
            style={{ background: "#2c3e50", border: "1px solid #2c3e50" }}
          >
            Cuentas
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        {/* Filtros */}
        <div className={styles.filterBar}>
          <select value={filtroCuenta} onChange={(e) => setFiltroCuenta(e.target.value)}>
            <option value="">Todas las cuentas</option>
            {cuentas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.numero_cuenta} ({c.moneda})
              </option>
            ))}
          </select>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            <option value="ingreso">Ingresos</option>
            <option value="egreso">Egresos</option>
          </select>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="procesado">Procesados</option>
            <option value="ignorado">Ignorados</option>
          </select>
          {modoFechaCustom ? (
            <>
              <input type="date" value={filtroFechaDesde} onChange={(e) => setFiltroFechaDesde(e.target.value)} />
              <input type="date" value={filtroFechaHasta} onChange={(e) => setFiltroFechaHasta(e.target.value)} />
              <button
                className={styles.btnLink}
                onClick={() => {
                  setModoFechaCustom(false);
                  setFiltroFechaDesde("");
                  setFiltroFechaHasta("");
                }}
              >
                Mes
              </button>
            </>
          ) : (
            <>
              <select value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)}>
                {generarPeriodos().map((p) => (
                  <option key={p} value={p}>
                    {getPeriodoLabel(p)}
                  </option>
                ))}
              </select>
              <button className={styles.btnLink} onClick={() => setModoFechaCustom(true)}>
                Rango
              </button>
            </>
          )}
          <button className={styles.btnSync} onClick={handleSync} disabled={sincronizando}>
            {sincronizando ? "Sincronizando..." : "Sincronizar"}
          </button>
        </div>

        {/* Resultado sync */}
        {syncResult && (
          <div className={styles.syncResult}>
            {(() => {
              const resultados = syncResult.resultados || [];
              const totalNuevos = resultados.reduce((s, r) => s + (r.nuevos || 0), 0);
              const detalle = resultados
                .map((r) => {
                  if (r.error) return `${r.cuenta}: error`;
                  if (r.nuevos === 0) return `${r.cuenta} (${r.moneda}): sin datos nuevos en Clay`;
                  return `${r.cuenta} (${r.moneda}): ${r.nuevos} nuevos`;
                })
                .join(" | ");
              return totalNuevos === 0
                ? `Sync OK — Clay no tiene movimientos nuevos aún (puede demorar hasta 48h en días no hábiles). ${detalle}`
                : `Sincronización completada — ${detalle}`;
            })()}
          </div>
        )}

        {/* Loading */}
        {loading && <Loader />}

        {/* Tabla */}
        {!loading && movimientos.length === 0 && (
          <div className={styles.empty}>No hay movimientos bancarios para los filtros seleccionados</div>
        )}

        {!loading && movimientos.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Descripción</th>
                <th className={styles.montoCell}>Monto</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id}>
                  <td>{formatFecha(m.fecha)}</td>
                  <td>
                    <span
                      className={`${styles.tipoChip} ${m.tipo === "ingreso" ? styles.tipoIngreso : styles.tipoEgreso}`}
                    >
                      {m.tipo}
                    </span>
                  </td>
                  <td className={styles.descripcionCell} title={m.descripcion_raw}>
                    {m.descripcion_raw}
                  </td>
                  <td
                    className={`${styles.montoCell} ${m.tipo === "ingreso" ? styles.montoIngreso : styles.montoEgreso}`}
                  >
                    {formatMonto(m.monto, m.moneda)}
                  </td>
                  <td>
                    <span
                      className={`${styles.estadoChip} ${
                        m.estado === "pendiente"
                          ? styles.chipPendiente
                          : m.estado === "procesado"
                            ? styles.chipProcesado
                            : styles.chipIgnorado
                      }`}
                    >
                      {m.estado}
                    </span>
                  </td>
                  <td>
                    {m.estado === "pendiente" && (
                      <div className={styles.acciones}>
                        <button className={styles.btnCorregir} onClick={() => setModalCorregir(m)}>
                          Clasificar
                        </button>
                        <button
                          className={styles.btnIgnorar}
                          onClick={() => handleIgnorar(m)}
                          disabled={procesando === m.id}
                        >
                          Ignorar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal corregir */}
      <CorregirMovimientoModal
        isOpen={!!modalCorregir}
        toggle={() => setModalCorregir(null)}
        movimiento={modalCorregir}
        onCorregido={cargarDatos}
      />

      {/* Modal Cuentas Bancarias */}
      <Modal isOpen={showCuentasModal} toggle={() => setShowCuentasModal(false)} size="lg">
        <ModalHeader toggle={() => setShowCuentasModal(false)}>Cuentas Bancarias</ModalHeader>
        <ModalBody>
          {cuentas.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "#999" }}>No hay cuentas registradas</div>
          ) : (
            <table style={{ width: "100%", fontSize: "0.82rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #dee2e6" }}>
                  <th style={{ padding: "8px 12px", textAlign: "left" }}>Banco</th>
                  <th style={{ padding: "8px 12px", textAlign: "left" }}>Cuenta</th>
                  <th style={{ padding: "8px 12px", textAlign: "center" }}>Moneda</th>
                  <th style={{ padding: "8px 12px", textAlign: "right" }}>Saldo actual</th>
                  <th style={{ padding: "8px 12px", textAlign: "center" }}>Último sync</th>
                </tr>
              </thead>
              <tbody>
                {cuentas.map((c) => (
                  <tr key={c.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px" }}>{c.bancos?.nombre || "Banco"}</td>
                    <td style={{ padding: "8px 12px" }}>{c.numero_cuenta}</td>
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      <span
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: "#e8f4fd",
                          color: "#2980b9",
                        }}
                      >
                        {c.moneda}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700 }}>
                      {formatMonto(c.saldo_actual, c.moneda)}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontSize: "0.75rem", color: "#999" }}>
                      {c.saldo_fecha ? new Date(c.saldo_fecha).toLocaleDateString("es-CL") : "Nunca"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default MovimientosBancarios;
